































































import { Component, Prop, Vue } from 'vue-property-decorator';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PreservationModel from '@/models/PreservationModel';
import { CompressedFile } from '@/types/compressedFile';
import { COMPRESSED_PRESERVATION_STATUSES, SURELOCK_SCOPES, ZIP_FILE_FIELDS } from "@/utils/constants";
import { getInstance } from "@/auth/authPlugin";
import Api from '@/store/surelockApi';
import {
  BAlert,
  BButton,
  BCard,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BOverlay,
  BSpinner,
} from 'bootstrap-vue';

@Component({
  components: {
    FontAwesomeIcon,
    BAlert,
    BButton,
    BCard,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BOverlay,
    BSpinner,
  },
})
export default class CompressedFiles extends Vue {
  @Prop() preservation!: PreservationModel;

  zippedFiles = new Array<CompressedFile>();
  password = '';
  status = '';
  loading = false;
  hidePassword = true;
  passwordInputType = 'password';
  lastDownloadError = '';

  retrievalUser = false;

  zipFileFields = [
    {
      name: 'Filename',
      prop: ZIP_FILE_FIELDS.S3_FILE_NAME,
    },
    {
      name: 'Size (mb)',
      prop: ZIP_FILE_FIELDS.COMPRESSED_SIZE,
    },
  ];

  async mounted(): Promise<void> {
    const authService = getInstance();
    this.retrievalUser = await authService.userHasSurelockScope(
      SURELOCK_SCOPES.RETRIEVE_PRESERVATION
    );

    await this.getCompressedData();
  }

  async getCompressedData(): Promise<void> {
    try {
      if (this.preservation?.id) {
        const data = await Api.getZippedFiles(this.preservation.id);
        this.zippedFiles = data.zips;
        this.password = data.password || '';
        this.status = data.status;
      }
    } catch (err) {
      const message = _.get(err, 'response.data.message') || 'Failed requesting zip files';
      this.$toasted.error(message, { duration: 3000 });
      return;
    }
  }

  statusIsSuccessful(): boolean {
    const isSuccessful = this.status === COMPRESSED_PRESERVATION_STATUSES.SUCCESSFUL;
    this.$emit('fillZippedCount', this.zippedFiles.length);
    this.$emit('fillCompressedStatus', this.status);

    if (isSuccessful) {
      this.$emit('fillPassword', this.password);
    }

    return isSuccessful;
  }

  setStatusMessage(): string {
    switch (this.status) {
      case COMPRESSED_PRESERVATION_STATUSES.ERROR:
        return 'There was an error zipping videos for this preservation.';
      case COMPRESSED_PRESERVATION_STATUSES.PROCESSING:
        return 'The preservation is currently being zipped. You can download the zipped videos when this is complete.';
      case COMPRESSED_PRESERVATION_STATUSES.NO_FILES_TO_COMPRESS:
        return 'There were no valid videos to zip for this preservation. Contact the Camera Cloud Team. If this has been resolved, try again with the Zip Videos button.'
      case COMPRESSED_PRESERVATION_STATUSES.NOT_FOUND:
        return 'The zip process was not initiated for this preservation. Use the Zip Videos button on the right to create the zip files.'
      default:
        return '';
    }
  }

  getValueForZip(zipFile: CompressedFile, value: string): any {
    if (value === ZIP_FILE_FIELDS.COMPRESSED_SIZE) {
      const size = _.get(zipFile, value, 0);
      return (size / (1024 * 1024)).toFixed(2);
    }
    
    return _.get(zipFile, value, 'N/A');
  }

  clickLink(link: HTMLAnchorElement): void {
    link.click();
  }

  async compress(): Promise<void> {
    try {
      if (this.preservation?.id) {
        await Api.compressPreservation(this.preservation.id);
      }
    } catch (err) {
      const response = err.response || null;
      let message: string;
      if (response?.statusText) {
        message = response.statusText;
      } else if (response?.status === 429) {
        message = 'Maximum number of compress requests reached this hour';
      } else {
        message = 'Compress request failed';
      }

      this.$toasted.error(message, {
          position: 'top-left',
          duration: 4000,
        });
    } finally {
      this.loading = false;
    }
  }

  async download(zip: CompressedFile): Promise<void> {
    this.lastDownloadError = '';
    this.loading = true;

    try {
      if (this.preservation && this.preservation.id) {
        const response = await Api.downloadPreservation(this.preservation.id, zip.s3FileName);
  
        const blob = new Blob([response.zipFile], { type: 'application/zip' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        
        // filename format: {preservationId}/compressed/{preservationId}-{start}-{end}.zip
        const splitZipName = zip.s3FileName.split('/');
        if (splitZipName.length !== 3) {
          this.$toasted.error('Invalid Zipfile Name', {
            position: 'top-left',
            duration: 4000,
          });
  
          return;
        }
  
        link.download = splitZipName[2];
        this.clickLink(link);
        URL.revokeObjectURL(link.href);
      }

    } catch (err) {
      const response = err.response || null;
      let message: string;
      if (response?.statusText) {
        message = response.statusText;
      } else if (response?.status === 409) {
        message = 'Cannot download files for a preservation you made';
      } else if (response?.status === 429) {
        message = 'Maximum number of download requests reached this hour';
      } else {
        message = 'Download request failed';
      }

      this.$toasted.error(message, {
        position: 'top-left',
        duration: 4000,
      });
      
      this.lastDownloadError = message;
    } finally {
      this.loading = false;
    }
  }

  canDownloadAll(): boolean {
    const videosInTimespan = this.preservation.videosInTimespan || 0;
    const hasVideos = videosInTimespan > 0;

    return hasVideos && this.statusIsSuccessful();
  }

  async downloadAll(): Promise<void> {
    if (this.canDownloadAll()) {
      for (let i = 0; i < this.zippedFiles.length; i++) {
        await this.download(this.zippedFiles[i]);
      }
    }
  }
}
